import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Typography, Spin, message, Space, Tag, Image, Button, Table, Modal } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, CalendarOutlined, EnvironmentOutlined, TrophyOutlined, FileOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import config, { SUBJECTS } from '../../config';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import AdminLayout from '../Layout/AdminLayout';
import { hasPermission } from '../../utils/permissions';

const { Title } = Typography;

const StudentProfileDetail = () => {
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [messageApi, contextHolder] = message.useMessage();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        fetchProfileDetail();
    }, [id]);

    const fetchProfileDetail = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${config.apiUrl}/api/v1/student-profile/${id}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            if (response.data.success) {

                const data = {
                    ...response.data.data,
                    date_of_birth: response?.data?.data?.date_of_birth ? dayjs(response?.data?.data?.date_of_birth).format('DD/MM/YYYY') : null,
                    full_name: response?.data?.data?.user?.name,
                    email: response?.data?.data?.user?.email,
                    student_phone_number: response?.data?.data?.user?.phone,
                    parents_phone_number: response?.data?.data?.parents_phone_number,
                    contact_address: response?.data?.data?.contact_address,
                    admission_area: response?.data?.data?.admission_area,
                    admission_group: response?.data?.data?.admission_group,
                    graduation_year: response?.data?.data?.graduation_year,
                    first_admission: response?.data?.data?.first_admission
                }                

                setProfile(data);
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            if (error.response?.status === 401) {
                messageApi.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!');
                navigate('/login');
            } else {
                messageApi.error('Không thể tải thông tin thí sinh. Vui lòng thử lại!');
            }
        } finally {
            setLoading(false);
        }
    };    

    const handlePreview = (attachment) => {
        setPreviewData(attachment);
        setPreviewOpen(true);
    };

    const isImageFile = (mimeType) => {
        return mimeType.startsWith('image/');
    };

    const isPdfFile = (mimeType) => {
        return mimeType === 'application/pdf';
    };

    const renderGrades = (grades) => {
        if (!grades) return null;

        const subjects = Object.entries(SUBJECTS).map(([key, label]) => ({
            key,
            label
        }));

        const columns = [
            {
                title: 'Môn học',
                dataIndex: 'label',
                key: 'label',
                width: 200,
                fixed: 'left'
            },
            {
                title: 'Lớp 10',
                dataIndex: 'grade_10',
                key: 'grade_10',
                align: 'center',
                render: (_, record) => grades[`${record.key}_10`] || 'N/A'
            },
            {
                title: 'Lớp 11',
                dataIndex: 'grade_11',
                key: 'grade_11',
                align: 'center',
                render: (_, record) => grades[`${record.key}_11`] || 'N/A'
            },
            {
                title: 'Lớp 12',
                dataIndex: 'grade_12',
                key: 'grade_12',
                align: 'center',
                render: (_, record) => grades[`${record.key}_12`] || 'N/A'
            }
        ];

        return (
            <Card title="Kết quả học tập" style={{ marginBottom: 16 }}>
                <Table
                    columns={columns}
                    dataSource={subjects}
                    rowKey="key"
                    pagination={false}
                    size="middle"
                    bordered
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        );
    };

    const renderAdmissions = (admissions, firstAdmission) => {
        // Create a combined array of admissions
        const allAdmissions = [
            // Add first admission if it exists
            ...(firstAdmission ? [{
                id: 'first_admission',
                priority: 0,
                subject: {
                    code: firstAdmission.code,
                    name: firstAdmission.name
                },
                isFirstAdmission: true
            }] : []),
            // Add regular admissions
            ...(admissions || [])
        ];

        if (allAdmissions.length === 0) return null;

        const columns = [
            {
                title: 'Nguyện vọng',
                dataIndex: 'priority',
                key: 'priority',
                width: 120,
                render: (priority, record) => record.isFirstAdmission ? 
                    <Tag color="green">Nguyện vọng 1</Tag> : 
                    `Nguyện vọng ${priority}`
            },
            {
                title: 'Mã ngành',
                dataIndex: ['subject', 'code'],
                key: 'code',
                width: 120
            },
            {
                title: 'Tên ngành',
                dataIndex: ['subject', 'name'],
                key: 'name',
                ellipsis: true
            }
        ];

        return (
            <Card title="Nguyện vọng xét tuyển" style={{ marginBottom: 16 }}>
                <Table
                    columns={columns}
                    dataSource={allAdmissions}
                    rowKey="id"
                    pagination={false}
                    size="middle"
                    bordered
                />
            </Card>
        );
    };

    const renderAttachments = (attachments) => {
        if (!attachments || attachments.length === 0) return null;

        const handleDownload = (attachment) => {
            const link = document.createElement('a');
            link.href = attachment.file_url;
            link.download = attachment.file_name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        return (
            <>
                <Card title="Tài liệu đính kèm" style={{ marginBottom: 16 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {attachments.map(attachment => (
                            <Card 
                                key={attachment.id} 
                                size="small" 
                                title={attachment.attachment_type.name}
                            >
                                <Space>
                                    <FileOutlined />
                                    <a 
                                        href={attachment.file_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                    >
                                        {attachment.file_name}
                                    </a>
                                    <Tag color="blue">
                                        {(attachment.file_size / 1024 / 1024).toFixed(2)} MB
                                    </Tag>
                                    <Button 
                                        type="link" 
                                        icon={<DownloadOutlined />}
                                        onClick={() => handleDownload(attachment)}
                                    >
                                        Tải xuống
                                    </Button>
                                    {(isImageFile(attachment.mime_type)) && (
                                        <Button 
                                            type="link" 
                                            icon={<EyeOutlined />}
                                            onClick={() => handlePreview(attachment)}
                                        >
                                            Xem trước
                                        </Button>
                                    )}
                                </Space>
                            </Card>
                        ))}
                    </Space>
                </Card>

                <Modal
                    title={previewData?.file_name}
                    open={previewOpen}
                    onCancel={() => setPreviewOpen(false)}
                    footer={null}
                    width={800}
                    style={{ top: 20 }}
                >
                    {previewData && (
                        <div style={{ textAlign: 'center' }}>
                            {isImageFile(previewData.mime_type) ? (
                                <Image
                                    src={previewData.file_url}
                                    alt={previewData.file_name}
                                    style={{ maxWidth: '100%', maxHeight: '70vh' }}
                                />
                            ) : isPdfFile(previewData.mime_type) ? (
                                <iframe
                                    src={previewData.file_url}
                                    style={{ width: '100%', height: '70vh', border: 'none' }}
                                    title={previewData.file_name}
                                />
                            ) : null}
                        </div>
                    )}
                </Modal>
            </>
        );
    };

    const renderPayment = (payment) => {
        if (!payment) return null;

        const handlePreviewPayment = () => {
            setPreviewData({
                file_url: payment.attachment,
                file_name: `Minh chứng nộp lệ phí - ${dayjs(payment.created_at).format('DD/MM/YYYY HH:mm')}`,
                mime_type: 'image/jpeg' // Assuming it's an image, adjust if needed
            });
            setPreviewOpen(true);
        };

        const handleDownloadPayment = () => {
            const link = document.createElement('a');
            link.href = payment.attachment;
            link.download = `minh-chung-le-phi-${profile.student_code}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        return (
            <Card title="Thông tin thanh toán lệ phí" style={{ marginBottom: 16 }}>
                <Descriptions bordered>
                    <Descriptions.Item label="Trạng thái" span={3}>
                        <Tag color={payment.is_paid ? 'green' : 'orange'}>
                            {payment.is_paid ? 'Đã nộp lệ phí' : 'Chưa xác nhận'}
                        </Tag>
                    </Descriptions.Item>
                    {payment.attachment && (
                        <>
                            <Descriptions.Item label="Minh chứng" span={3}>
                                <Space>
                                    <FileOutlined />
                                    <span>Hình ảnh minh chứng đã tải lên</span>
                                    <Button 
                                        type="link" 
                                        icon={<EyeOutlined />}
                                        onClick={handlePreviewPayment}
                                    >
                                        Xem trước
                                    </Button>
                                    <Button 
                                        type="link" 
                                        icon={<DownloadOutlined />}
                                        onClick={handleDownloadPayment}
                                    >
                                        Tải xuống
                                    </Button>
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="Ngày tải lên" span={3}>
                                <Space>
                                    <CalendarOutlined />
                                    {dayjs(payment.created_at).format('DD/MM/YYYY HH:mm')}
                                </Space>
                            </Descriptions.Item>
                        </>
                    )}
                </Descriptions>
            </Card>
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" />
                </div>
            );
        }

        if (!profile) {
            return (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Title level={4}>Không tìm thấy thông tin thí sinh</Title>
                </div>
            );
        }

        return (
            <>
                {contextHolder}
                <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
                    Thông tin chi tiết thí sinh
                </Title>

                <Card style={{ marginBottom: 16 }}>
                    <Descriptions title="Thông tin cá nhân" bordered>
                        <Descriptions.Item label="Họ và tên" span={3}>
                            <Space>
                                <UserOutlined />
                                {profile.full_name}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Mã hồ sơ">
                            {profile.student_code}
                        </Descriptions.Item>
                        <Descriptions.Item label="CCCD/CMND">
                            {profile.identification}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ngày sinh">
                            <Space>
                                <CalendarOutlined />
                                {profile.date_of_birth}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Email" span={2}>
                            <Space>
                                <MailOutlined />
                                {profile.user.email}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Số điện thoại">
                            <Space>
                                <PhoneOutlined />
                                {profile.student_phone_number}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Số điện thoại phụ huynh">
                            <Space>
                                <PhoneOutlined />
                                {profile.parents_phone_number}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Địa chỉ liên hệ" span={3}>
                            <Space>
                                <EnvironmentOutlined />
                                {profile.contact_address}
                            </Space>
                        </Descriptions.Item>
                    </Descriptions>
                </Card>

                {hasPermission('view_student_full_profile') && (
                <Card style={{ marginBottom: 16 }}>
                    <Descriptions title="Thông tin tuyển sinh" bordered>
                        <Descriptions.Item label="Khu vực">
                            {profile.admission_area}
                        </Descriptions.Item>
                        <Descriptions.Item label="Đối tượng">
                            {profile.admission_group}
                        </Descriptions.Item>
                        <Descriptions.Item label="Năm tốt nghiệp">
                            {profile.graduation_year}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ngành xét tuyển ưu tiên">
                            {profile.first_admission?.name}
                        </Descriptions.Item>
                        <Descriptions.Item label="Điểm IELTS">
                            {profile.ielts_grade || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Giải thưởng HSG cấp tỉnh">
                            {profile.provincial_prize ? (
                                <Space>
                                    <TrophyOutlined style={{ color: '#faad14' }} />
                                    <Tag color={profile.provincial_prize === 'first' ? 'gold' : profile.provincial_prize === 'second' ? 'blue' : 'orange'}>
                                        {profile.provincial_prize_label ?? profile.provincial_prize}
                                    </Tag>
                                    {profile.provincial_prize_subject && (
                                        <Tag color="green">
                                            {profile.provincial_prize_subject.name}
                                        </Tag>
                                    )}
                                    {profile.provincial_prize_grade && (
                                        <Tag>Lớp {profile.provincial_prize_grade}</Tag>
                                    )}
                                </Space>
                            ) : 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Xếp loại lớp 10">
                            <Tag color={profile.grade_10_rank === 'excellent' ? 'green' : 'blue'}>
                                {profile.grade_10_rank === 'excellent' ? 'Giỏi' : 'Khác'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Xếp loại lớp 11">
                            <Tag color={profile.grade_11_rank === 'excellent' ? 'green' : 'blue'}>
                                {profile.grade_11_rank === 'excellent' ? 'Giỏi' : 'Khác'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Xếp loại lớp 12">
                            <Tag color={profile.grade_12_rank === 'excellent' ? 'green' : 'blue'}>
                                {profile.grade_12_rank === 'excellent' ? 'Giỏi' : 'Khác'}
                            </Tag>
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
                )}

                {renderGrades(profile.grades)}
                {renderAdmissions(profile.admissions, profile.first_admission)}
                {renderAttachments(profile.attachments)}
                {renderPayment(profile.payment)}

                <div style={{ textAlign: 'center', marginTop: 24 }}>
                    <Button type="primary" onClick={() => navigate(-1)}>
                        Quay lại
                    </Button>
                </div>
            </>
        );
    };

    return (
        <AdminLayout>
            <div style={{ padding: '24px' }}>
                {renderContent()}
            </div>
        </AdminLayout>
    );
};

export default StudentProfileDetail; 